import { useCookieConsent } from '@/contexts/CookieContext';
import { CookiePreferences } from './CookiePreferences';
import { useLanguage } from '@/contexts/LanguageContext';
import { getLocalizedRoute } from '@/lib/routes';
import PillButton from '@/components/ui/PillButton';
import { SITE_CONFIG } from '@/config/site';

/**
 * First-visit consent card, bottom left. Rejecting is exactly as prominent as
 * accepting: two equal pills side by side, with the detailed preferences one
 * quiet link below.
 */
export const CookieConsent: React.FC = () => {
  const { hasConsented, acceptAll, rejectAll, openPreferences } = useCookieConsent();
  const { t, language } = useLanguage();

  if (hasConsented) {
    return null;
  }

  return (
    <>
      <div
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
        className="fixed bottom-4 left-4 right-4 z-50 rounded-md border border-white/10 bg-ink p-5 text-white shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500 sm:right-auto sm:max-w-sm"
      >
        <h2 id="cookie-consent-title" className="label-mono text-white/55">
          {t('cookieBanner.title')}
        </h2>
        <p id="cookie-consent-description" className="mt-3 text-sm leading-relaxed text-white/75">
          {t('cookieBanner.text')}{' '}
          <a
            href={getLocalizedRoute(SITE_CONFIG.routes.privacy, language)}
            className="text-white underline underline-offset-2 hover:text-white/80"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('cookieBanner.policy')}
          </a>
          .
        </p>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <PillButton size="sm" tone="light" onClick={rejectAll} aria-label={t('cookieBanner.rejectLabel')}>
            {t('cookieBanner.reject')}
          </PillButton>
          <PillButton size="sm" tone="light" onClick={acceptAll} aria-label={t('cookieBanner.acceptLabel')}>
            {t('cookieBanner.accept')}
          </PillButton>
        </div>
        <button
          type="button"
          onClick={openPreferences}
          className="label-mono mt-4 text-white/55 underline underline-offset-4 transition-colors hover:text-white"
        >
          {t('cookieBanner.manage')}
        </button>
      </div>

      <CookiePreferences />
    </>
  );
};
